/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";
import { HL7ServiceData, HL7ServiceFieldProps } from "@advapacs/common";

import {
  EMPTY,
  EQUAL,
  EXISTS,
  getSelectStyles,
  GREATER,
  GREATER_OR_EQUAL,
  HL7,
  IN_RANGE,
  LESS,
  LESS_OR_EQUAL,
  LIKE,
  NOT_EMPTY,
  NOT_EQUAL,
  NOT_EXISTS,
  NOT_LIKE,
  OUT_OF_RANGE,
} from "../../../constant";
import Validator from "../validator";
import HL7FieldInput from "../../Input/HL7FieldInput";

const HL7ServiceField: FunctionComponent<HL7ServiceFieldProps> = ({
  disabled,
  id,
  parent,
  errors,
  hl7,
  onValueChangeHandler,
  isDark,
}: HL7ServiceFieldProps) => {
  const { t } = useTranslation();

  const dispatchNewValue = (hl7ServiceData: HL7ServiceData) => {
    onValueChangeHandler(
      {
        scope: "conditions",
        id,
        type: HL7,
        hl7: hl7ServiceData,
      },
      parent
    );
  };

  const updateHL7Field = async (selected: string) => {
    dispatchNewValue({ ...hl7, path: selected });
  };

  const updateValue = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: hl7?.operator,
      path: hl7?.path,
      value: event.currentTarget.value,
    });
  };

  const updateFrom = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: hl7?.operator,
      path: hl7?.path,
      to: hl7?.to,
      from: event.currentTarget.value,
    });
  };

  const updateTo = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: hl7?.operator,
      path: hl7?.path,
      from: hl7?.from,
      to: event.currentTarget.value,
    });
  };

  const selectOperator = (update: any) => {
    dispatchNewValue({ ...hl7, operator: update?.value });
  };

  const options = [
    { label: t("rule:empty"), value: EMPTY },
    { label: t("rule:equals"), value: EQUAL },
    { label: t("rule:exists"), value: EXISTS },
    { label: t("rule:greater"), value: GREATER },
    { label: t("rule:greater_or_equal"), value: GREATER_OR_EQUAL },
    { label: t("rule:in_range"), value: IN_RANGE },
    { label: t("rule:less"), value: LESS },
    { label: t("rule:less_or_equal"), value: LESS_OR_EQUAL },
    { label: t("rule:like"), value: LIKE },
    { label: t("rule:not_empty"), value: NOT_EMPTY },
    { label: t("rule:not_equal"), value: NOT_EQUAL },
    { label: t("rule:not_exists"), value: NOT_EXISTS },
    { label: t("rule:not_like"), value: NOT_LIKE },
    { label: t("rule:out_of_range"), value: OUT_OF_RANGE },
  ];

  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === HL7) !== undefined
    );
  };

  return (
    <div className="row">
      <div className="col-md-4 col-xs-12 mb-1">
        <HL7FieldInput
          placeholder={t("form:hl7.hl7_field")}
          onChange={updateHL7Field}
          initialValue={hl7?.path}
          disabled={disabled}
        />

        {hasError() && !hl7?.path && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_hl7_field`}
          >
            {t("form:hl7.field_required")}
          </label>
        )}
        {hasError() && hl7?.path && !Validator.validateHL7Field(hl7?.path) && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_hl7_field`}
          >
            {t("form:hl7.hl7_field_format_invalid")}
          </label>
        )}
      </div>
      <div className="select mb-1 col-md-4 col-xs-12">
        <Select
          menuPlacement="auto"
          menuPosition="fixed"
          styles={getSelectStyles(isDark)}
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          className="form-control-select"
          options={options}
          placeholder={t("form:hl7.select_operator")}
          onChange={selectOperator}
          value={options.find((option) => option?.value === hl7?.operator)}
        />
        {hasError() && !hl7?.operator && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_operator`}
          >
            {t("form:hl7.field_required")}
          </label>
        )}
      </div>
      {hl7?.operator &&
        [
          EQUAL,
          GREATER,
          GREATER_OR_EQUAL,
          LESS,
          LESS_OR_EQUAL,
          LIKE,
          NOT_EQUAL,
          NOT_LIKE,
        ].includes(hl7?.operator) && (
          <div className="col-md-3 col-xs-12">
            <input
              disabled={disabled}
              id={`${id}_value`}
              type="text"
              name="value"
              className="form-control mb-1"
              placeholder={t("form:hl7.value")}
              onChange={updateValue}
              value={hl7?.value}
            />
            {hasError() && !hl7?.value && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_value`}
              >
                {t("form:hl7.field_required")}
              </label>
            )}
          </div>
        )}
      {hl7?.operator && [IN_RANGE, OUT_OF_RANGE].includes(hl7?.operator) && (
        <>
          <div className="col-md-4 col-xs-12 mb-1">
            <input
              disabled={disabled}
              type="text"
              name="from"
              id={`${id}_from`}
              className="form-control mb-1"
              value={hl7?.from}
              placeholder={t("form:hl7.from")}
              onChange={updateFrom}
            />
            {hasError() && !hl7?.from && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_from`}
              >
                {t("form:hl7.field_required")}
              </label>
            )}
          </div>
          <div className="col">
            <input
              disabled={disabled}
              type="text"
              name="to"
              id={`${id}_to`}
              className="form-control mb-1"
              value={hl7?.to}
              placeholder={t("form:hl7.to")}
              onChange={updateTo}
            />
            {hasError() && !hl7?.to && (
              <label className="validation-invalid-label" htmlFor={`${id}_to`}>
                {t("form:hl7.field_required")}
              </label>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HL7ServiceField;
